import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import type { ChecklistAudience, ChecklistDismissReason } from 'src/analytics'
import { JourneyScope } from 'src/analytics'
import {
  applicationsApplicationsListOptions,
  JourneyActionMethodEnum,
  JourneyActionStatusEnum,
  OnboardingJourneyStatusEnum,
  onboardingJourneysPartialUpdateMutation,
  onboardingJourneysRetrieveOptions,
  onboardingJourneysRetrieveQueryKey,
  RoleVariantEnum,
  type Application,
  type OnboardingJourney,
  type PatchedJourneyAction
} from 'src/api/main'
import { paths } from 'src/router'
import { normalizeListResults } from 'src/utils'

import { checklistCard, JOURNEY_STALE_MS, SNOOZE_DAYS } from './constants'
import type { ChecklistItemModel, ChecklistTarget } from './types'
import { isStepSettled, toJourneyView } from './utils'

/**
 * Which of the two checklists the platform decided this person gets.
 *
 * The role variant is chosen server-side from the manifest, so the widget no
 * longer works it out from the sidebar's access tier — two answers to one
 * question is one answer too many, and the statuses being drawn belong to the
 * variant the platform picked.
 */
const AUDIENCE: Record<RoleVariantEnum, ChecklistAudience> = {
  [RoleVariantEnum.ADMIN]: 'admin',
  [RoleVariantEnum.MEMBER]: 'member'
}

type TargetContext = {
  workspaceId: number | undefined
  apps: Application[]
  resolvePath: (menuKey: string) => string | undefined
}

const resolveTargetPath = (
  target: ChecklistTarget,
  { workspaceId, apps, resolvePath }: TargetContext
): string | undefined => {
  if (target.kind === 'account') return target.path
  if (target.kind === 'menu') return resolvePath(target.menuKey)

  const app = apps.find((item) => item.instance_type?.type === target.appType)
  if (!app || workspaceId == null) return undefined

  return `${paths.workspaces.apps.details(String(workspaceId), String(app.id))}?section=${target.section}`
}

type Args = {
  workspaceId: number | undefined
  /**
   * Resolves a sidebar menu key to its path, or `undefined` when the user cannot
   * open it. Passed in rather than imported: the sidebar has already applied the
   * access-role and feature-flag rules, and this widget must not learn them a
   * second time.
   */
  resolvePath: (menuKey: string) => string | undefined
}

/**
 * The checklist model, read from and written back to the platform.
 *
 * Nothing here decides whether a task is finished. The journey says so, and the
 * only way a status changes is a PATCH that says so back — which is also what
 * makes the same progress visible from another browser, and what lets the
 * platform emit `onboarding_step_completed` from the one place that knows.
 *
 * Every action answers with the whole updated journey, so each one is written
 * straight into the query cache: the panel repaints from the platform's own
 * copy rather than from an optimistic guess that a refetch might contradict.
 */
export const useGettingStarted = ({ workspaceId, resolvePath }: Args) => {
  const queryClient = useQueryClient()

  const request = useMemo(
    () => ({
      path: { scope: JourneyScope.CORE },
      // Omitted rather than sent as a placeholder: with no workspace the query
      // is disabled anyway, and a zero here would key the cache on a workspace
      // that does not exist.
      ...(workspaceId != null ? { query: { organization: workspaceId } } : {})
    }),
    [workspaceId]
  )

  const journeyQuery = useQuery({
    ...onboardingJourneysRetrieveOptions(request),
    enabled: workspaceId != null,
    staleTime: JOURNEY_STALE_MS,
    select: toJourneyView
  })

  const { mutateAsync: patchJourney } = useMutation(
    onboardingJourneysPartialUpdateMutation()
  )

  const view = journeyQuery.data
  const journey = view?.journey

  /**
   * The workspace's installed applications, asked for only when a step actually
   * points inside one. Roles live in the CRM's own settings now, and the id in
   * that URL is this workspace's installation — there is no way to build the
   * path without looking it up.
   */
  const needsApps = (journey?.steps ?? []).some(
    (step) => checklistCard(step.key)?.target.kind === 'app'
  )

  const appsQuery = useQuery({
    ...applicationsApplicationsListOptions({
      query: { organization: workspaceId ?? 0, per_page: 100 }
    }),
    enabled: workspaceId != null && needsApps,
    staleTime: JOURNEY_STALE_MS
  })

  const apps = normalizeListResults(appsQuery.data)

  const report = useCallback(
    async (body: PatchedJourneyAction) => {
      if (workspaceId == null) return

      const next = await patchJourney({ ...request, body })
      queryClient.setQueryData<OnboardingJourney>(
        onboardingJourneysRetrieveQueryKey(request),
        next
      )
    },
    [patchJourney, queryClient, request, workspaceId]
  )

  const audience: ChecklistAudience = journey
    ? AUDIENCE[journey.role_variant]
    : 'member'

  const items = useMemo<ChecklistItemModel[]>(
    () =>
      (journey?.steps ?? []).flatMap((step) => {
        const def = checklistCard(step.key)
        // A step this build has no card for — the manifest moved ahead of the
        // frontend. Skipping it beats drawing an unlabelled row.
        if (!def) return []

        const path = resolveTargetPath(def.target, {
          workspaceId,
          apps,
          resolvePath
        })

        // A step whose page this build does not offer — a feature flag is off,
        // or the access role hides it. The manifest is workspace-wide and knows
        // none of that, so the filtering happens here: a task the user cannot
        // open is worse than a task they are not shown, and the old widget hid
        // it for the same reason.
        if (!path) return []

        return [
          {
            def,
            path,
            status: step.status,
            done: isStepSettled(step),
            errorCode: step.error_code
          }
        ]
      }),
    [apps, journey, resolvePath, workspaceId]
  )

  const total = items.length
  const completedCount = items.filter((item) => item.done).length

  /**
   * Put away for a reason no repaint can change. `isSnoozed` compares the
   * snooze against the clock inside `toJourneyView`, so nothing here reads the
   * time during render.
   */
  const dormant =
    view === undefined ||
    total === 0 ||
    view.isSnoozed ||
    view.journey.dismissed_at !== null ||
    view.journey.status === OnboardingJourneyStatusEnum.COMPLETED

  /**
   * Closes a step the platform cannot see finish. Only the cards marked
   * `closedByOpening` take this route; everything else is settled by the
   * platform watching its own data, and a click would be a lie about it.
   */
  const complete = useCallback(
    (item: ChecklistItemModel) => {
      if (!item.def.closedByOpening || item.done) return

      void report({
        action: 'set_step',
        step_key: item.def.key,
        status: JourneyActionStatusEnum.DONE,
        method: JourneyActionMethodEnum.CHECKLIST
      })
    },
    [report]
  )

  const hideForNow = useCallback(
    () => void report({ action: 'hide', days: SNOOZE_DAYS }),
    [report]
  )

  const hideForGood = useCallback(
    (reason: ChecklistDismissReason) =>
      void report({ action: 'dismiss', reason }),
    [report]
  )

  return {
    isLoading: journeyQuery.isLoading || appsQuery.isLoading,
    visible: !dormant,
    audience,
    items,
    completedCount,
    total,
    complete,
    hideForNow,
    hideForGood
  }
}
